/**
 * User engagement tracking utilities
 */

/**
 * Track time spent on page
 */
export class TimeTracker {
  private startTime: number;
  private totalTime: number = 0;
  private isActive: boolean = true;
  private visibilityChangeHandler: (() => void) | null = null;

  constructor() {
    this.startTime = Date.now();
    this.setupVisibilityTracking();
  }

  private setupVisibilityTracking(): void {
    this.visibilityChangeHandler = () => {
      if (document.hidden) {
        // Page is hidden, pause timer
        this.totalTime += Date.now() - this.startTime;
        this.isActive = false;
      } else {
        // Page is visible again, resume timer
        this.startTime = Date.now();
        this.isActive = true;
      }
    };

    document.addEventListener('visibilitychange', this.visibilityChangeHandler);
  }

  /**
   * Get total time spent on page (in seconds)
   */
  getTimeSpent(): number {
    const currentTime = this.isActive ? Date.now() - this.startTime : 0;
    return Math.round((this.totalTime + currentTime) / 1000);
  }

  /**
   * Cleanup
   */
  destroy(): void {
    if (this.visibilityChangeHandler) {
      document.removeEventListener('visibilitychange', this.visibilityChangeHandler);
    }
  }
}

/**
 * Track scroll depth
 */
export class ScrollTracker {
  private maxScrollDepth: number = 0;
  private scrollHandler: (() => void) | null = null;
  private milestones: Set<number> = new Set();

  constructor() {
    this.updateScrollDepth();
    this.setupScrollTracking();
  }

  private setupScrollTracking(): void {
    this.scrollHandler = () => {
      this.updateScrollDepth();
    };

    window.addEventListener('scroll', this.scrollHandler, { passive: true });
  }

  private updateScrollDepth(): void {
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const windowHeight = window.innerHeight;
    const documentHeight = document.documentElement.scrollHeight;

    const scrollDepth = Math.round(
      ((scrollTop + windowHeight) / documentHeight) * 100
    );

    this.maxScrollDepth = Math.max(this.maxScrollDepth, Math.min(scrollDepth, 100));

    // Track milestone percentages (25%, 50%, 75%, 100%)
    [25, 50, 75, 100].forEach(milestone => {
      if (this.maxScrollDepth >= milestone && !this.milestones.has(milestone)) {
        this.milestones.add(milestone);
      }
    });
  }

  /**
   * Get maximum scroll depth percentage
   */
  getMaxScrollDepth(): number {
    return this.maxScrollDepth;
  }

  /**
   * Get scroll milestones reached
   */
  getMilestones(): number[] {
    return Array.from(this.milestones).sort((a, b) => a - b);
  }

  /**
   * Cleanup
   */
  destroy(): void {
    if (this.scrollHandler) {
      window.removeEventListener('scroll', this.scrollHandler);
    }
  }
}

/**
 * Track mouse/touch interactions
 */
export class InteractionTracker {
  private clickCount: number = 0;
  private moveCount: number = 0;
  private lastMoveTime: number = 0;
  private clickHandler: (() => void) | null = null;
  private moveHandler: (() => void) | null = null;

  constructor() {
    this.setupInteractionTracking();
  }

  private setupInteractionTracking(): void {
    this.clickHandler = () => {
      this.clickCount++;
    };

    this.moveHandler = () => {
      const now = Date.now();
      // Throttle move counting (count at most once per second)
      if (now - this.lastMoveTime > 1000) {
        this.moveCount++;
        this.lastMoveTime = now;
      }
    };

    document.addEventListener('click', this.clickHandler);
    document.addEventListener('mousemove', this.moveHandler, { passive: true });
    document.addEventListener('touchmove', this.moveHandler, { passive: true });
  }

  /**
   * Get interaction metrics
   */
  getMetrics() {
    return {
      clicks: this.clickCount,
      moves: this.moveCount,
    };
  }

  /**
   * Cleanup
   */
  destroy(): void {
    if (this.clickHandler) {
      document.removeEventListener('click', this.clickHandler);
    }
    if (this.moveHandler) {
      document.removeEventListener('mousemove', this.moveHandler);
      document.removeEventListener('touchmove', this.moveHandler);
    }
  }
}

/**
 * Track page visibility and tab focus
 */
export class VisibilityTracker {
  private visibleTime: number = 0;
  private startTime: number = Date.now();
  private isVisible: boolean = !document.hidden;
  private focusCount: number = 0;
  private blurCount: number = 0;
  private visibilityHandler: (() => void) | null = null;
  private focusHandler: (() => void) | null = null;
  private blurHandler: (() => void) | null = null;

  constructor() {
    this.setupVisibilityTracking();
  }

  private setupVisibilityTracking(): void {
    this.visibilityHandler = () => {
      if (document.hidden) {
        this.visibleTime += Date.now() - this.startTime;
        this.isVisible = false;
        this.blurCount++;
      } else {
        this.startTime = Date.now();
        this.isVisible = true;
        this.focusCount++;
      }
    };

    this.focusHandler = () => {
      this.focusCount++;
    };

    this.blurHandler = () => {
      this.blurCount++;
    };

    document.addEventListener('visibilitychange', this.visibilityHandler);
    window.addEventListener('focus', this.focusHandler);
    window.addEventListener('blur', this.blurHandler);
  }

  /**
   * Get visibility metrics
   */
  getMetrics() {
    const currentVisible = this.isVisible ? Date.now() - this.startTime : 0;
    return {
      totalVisibleTime: Math.round((this.visibleTime + currentVisible) / 1000),
      focusCount: this.focusCount,
      blurCount: this.blurCount,
      isCurrentlyVisible: this.isVisible,
    };
  }

  /**
   * Cleanup
   */
  destroy(): void {
    if (this.visibilityHandler) {
      document.removeEventListener('visibilitychange', this.visibilityHandler);
    }
    if (this.focusHandler) {
      window.removeEventListener('focus', this.focusHandler);
    }
    if (this.blurHandler) {
      window.removeEventListener('blur', this.blurHandler);
    }
  }
}

/**
 * Comprehensive engagement tracker
 */
export class EngagementTracker {
  private timeTracker: TimeTracker;
  private scrollTracker: ScrollTracker;
  private interactionTracker: InteractionTracker;
  private visibilityTracker: VisibilityTracker;

  constructor() {
    this.timeTracker = new TimeTracker();
    this.scrollTracker = new ScrollTracker();
    this.interactionTracker = new InteractionTracker();
    this.visibilityTracker = new VisibilityTracker();
  }

  /**
   * Get all engagement metrics
   */
  getMetrics() {
    const time = this.timeTracker.getTimeSpent();
    const scroll = this.scrollTracker.getMaxScrollDepth();
    const interactions = this.interactionTracker.getMetrics();
    const visibility = this.visibilityTracker.getMetrics();

    // Calculate engagement score (0-100)
    const engagementScore = this.calculateEngagementScore(
      time,
      scroll,
      interactions.clicks,
      visibility.totalVisibleTime
    );

    return {
      timeSpent: time,
      activeTime: visibility.totalVisibleTime,
      scrollDepth: scroll,
      scrollMilestones: this.scrollTracker.getMilestones(),
      clicks: interactions.clicks,
      mouseMoves: interactions.moves,
      focusCount: visibility.focusCount,
      blurCount: visibility.blurCount,
      engagementScore,
      isEngaged: engagementScore > 50,
    };
  }

  /**
   * Calculate engagement score based on multiple factors
   */
  private calculateEngagementScore(
    timeSpent: number,
    scrollDepth: number,
    clicks: number,
    activeTime: number
  ): number {
    // Time score (0-30 points, max at 5 minutes)
    const timeScore = Math.min((timeSpent / 300) * 30, 30);

    // Scroll score (0-30 points)
    const scrollScore = (scrollDepth / 100) * 30;

    // Interaction score (0-20 points, max at 10 clicks)
    const interactionScore = Math.min((clicks / 10) * 20, 20);

    // Active time ratio score (0-20 points)
    const activeRatio = timeSpent > 0 ? activeTime / timeSpent : 0;
    const activeScore = activeRatio * 20;

    return Math.min(Math.round(timeScore + scrollScore + interactionScore + activeScore), 100);
  }

  /**
   * Cleanup all trackers
   */
  destroy(): void {
    this.timeTracker.destroy();
    this.scrollTracker.destroy();
    this.interactionTracker.destroy();
    this.visibilityTracker.destroy();
  }
}
